import React, { useCallback } from "react";

import { Divider, Grid } from "@material-ui/core";
import { Form, getIn, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import {
  ManualAdjustmentAction,
  openDispatchingModal,
  setModalContext,
  uploadRequest,
} from "actions";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import {
  BusinessFieldWithFilter,
  ManualChargeLabelSelectorField,
} from "components/Form/Fields";
import {
  CheckBox,
  FileInput,
  Input,
  Label,
  SelectField,
  SelectFieldOption,
  TextArea,
} from "components/Form/FormikControls";
import { HelpText } from "components/Form/FormikControls/HelpText";
import { Cell, Row } from "components/Layout";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { SundryPricingForm } from "components/Sundry/Form/SundryPricingForm";
import { SmallText } from "components/Text";

import { AuditLogTypes } from "constants/auditLog";
import { BaseTaxTypeOptions } from "constants/billing";
import { BUYER, IN_SALE, VENDOR } from "constants/businesses";
import { BusinessModalSection, ModalTypes } from "constants/navigation";
import {
  MANUAL_TEMPLATE_ID,
  SundryPartyType,
  SundryPartyTypeLookup,
  SundryTemplateLabel,
} from "constants/sundryTemplates";

import { buildSundryTemplateOptions } from "lib/sundries";

import {
  getBusinessById,
  getShouldChargeGSTByBusinessId,
  getSundryTemplates,
  selectMyLedgerAccountOptionsForLedgerEntryForm,
} from "selectors";

import {
  getSundryTemplateById,
  getRelevantSundryTemplatesByBillingDocumentId,
} from "selectors/sundryTemplate";

import { useBoolean, UseBooleanReturn } from "hooks";
import { uniq } from "lodash";

import {
  EMPTY_ARRAY,
  EMPTY_OBJECT_FUNCTION,
  getDollarPriceStringFromCents,
} from "lib";
import { DocumentTypes } from "constants/documentTypes";
import { DeletableThumbnail } from "components/Media";
import theme from "constants/theme";
import { SundryFormValues } from "./types";

const getPaymentDescription = amount => (amount > 0 ? "credited" : "debited");

interface SundryHelpTextProps {
  template: SundryTemplate;
  amount: number;
  paidFromBusinessName: string;
  paidToBusinessName: string;
}

const SundryHelpText = ({
  template,
  amount,
  paidFromBusinessName,
  paidToBusinessName,
}: SundryHelpTextProps) => {
  const { label }: SundryTemplate = template;
  const amountString: string = `$${getDollarPriceStringFromCents(Math.abs(amount))}`;

  const { fromType, toType }: ValueInObj<typeof SundryPartyTypeLookup> =
    SundryPartyTypeLookup[label];

  return (
    <HelpText>
      <ul className="text-left">
        {toType !== SundryPartyType.DEPLOYMENT && (
          <li>
            {`${paidToBusinessName} will be ${getPaymentDescription(amount)} ${amountString} on their ${toType}`}
          </li>
        )}
        {fromType !== SundryPartyType.DEPLOYMENT && (
          <li>
            {`${paidFromBusinessName} will be ${getPaymentDescription(amount * -1)}  ${amountString} on their ${fromType}`}
          </li>
        )}
      </ul>
    </HelpText>
  );
};

const isGstApplicableMap = {
  true: "Applicable",
  false: "Not Applicable",
  undefined: "Unknown",
  null: "Unknown",
} as const;

interface SundryFormProps {
  id: string | null;
  onClose: () => void;
  readOnlyFields?: string[];
  namespace?: string;
  billingDocumentId?: string | null;
  attachments?: File[];
  setDeleteFileDialog: (attachment: File) => void;
  setPreview: (id: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Parked attachment files state
  parkedAttachmentFiles?: any;
  setParkedAttachmentFiles?: (files: File[]) => void;
}

const getBusinessFilters = businessFilter => {
  // If the sundry template is filtering on Buyers or Vendors, assume they
  // want to also filter for in sale.
  const businessFilters = [BUYER, VENDOR].includes(businessFilter)
    ? [businessFilter, IN_SALE]
    : [businessFilter];
  return businessFilters.filter(Boolean);
};

const SundryForm: React.FC<SundryFormProps> = ({
  id,
  onClose,
  readOnlyFields = EMPTY_ARRAY,
  namespace: ns = "",
  billingDocumentId,
  attachments,
  parkedAttachmentFiles = EMPTY_ARRAY,
  setDeleteFileDialog,
  setPreview,
  setParkedAttachmentFiles,
}: SundryFormProps) => {
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const { values } = useFormikContext<SundryFormValues>();

  const fieldValues = getIn(values, ns);
  const {
    fromBusinessId,
    toBusinessId,
    sundryTemplateId,
    totalInc,
    createAnother,
  }: SundryFormValues = fieldValues;

  const activeSundryTemplate: SundryTemplate = useSelector(
    getSundryTemplateById(sundryTemplateId),
  );
  const isManual: boolean =
    sundryTemplateId &&
    sundryTemplateId.toString() === MANUAL_TEMPLATE_ID.toString();

  const ledgerAccountOptions: SelectFieldOption[] = useSelector(
    selectMyLedgerAccountOptionsForLedgerEntryForm,
  );

  const allSundryTemplates: Record<string, SundryTemplate> =
    useSelector(getSundryTemplates);

  const sundryTemplatesForBillingDocument: SundryTemplate[] = useSelector(
    getRelevantSundryTemplatesByBillingDocumentId(billingDocumentId),
  );

  const [
    isShowingConfirmDelete,
    showConfirmDelete,
    hideConfirmDelete,
  ]: UseBooleanReturn = useBoolean(false);

  const shouldFromBusinessChargeGST: ValueInObj<typeof isGstApplicableMap> =
    useSelector(getShouldChargeGSTByBusinessId(fromBusinessId));

  const fromBusiness: Business | null = useSelector(
    getBusinessById(fromBusinessId),
  );
  const toBusiness: Business | null = useSelector(
    getBusinessById(toBusinessId),
  );

  const fromBusinessGSTStatus: string =
    isGstApplicableMap[
      shouldFromBusinessChargeGST as keyof typeof isGstApplicableMap
    ];

  const returnTo: string = window.location.hash;

  const onConfirmDelete = () => {
    if (id) {
      dispatch(ManualAdjustmentAction.delete(id));
    }
    onClose();
  };

  function openEditFromBusiness() {
    dispatch(setModalContext(ModalTypes.ManualAdjustmentForm, fieldValues));
    dispatch(
      openDispatchingModal(ModalTypes.EditBusiness, returnTo, fieldValues, {
        businessId: fromBusinessId,
        section: BusinessModalSection.FINANCE_AND_ACCOUNTING,
      }),
    );
  }

  function openEditToBusiness() {
    dispatch(setModalContext(ModalTypes.ManualAdjustmentForm, fieldValues));
    dispatch(
      openDispatchingModal(ModalTypes.EditBusiness, returnTo, fieldValues, {
        businessId: toBusinessId,
        section: BusinessModalSection.FINANCE_AND_ACCOUNTING,
      }),
    );
  }

  const sundryTemplateOptions: SelectFieldOption[] = billingDocumentId
    ? buildSundryTemplateOptions(sundryTemplatesForBillingDocument)
    : buildSundryTemplateOptions(Object.values(allSundryTemplates));

  const isTemplateInUse: boolean = Object.values(SundryTemplateLabel).includes(
    activeSundryTemplate.templateLabel,
  );

  const renderHelpText: boolean = Boolean(
    fromBusinessId && toBusinessId && totalInc && isTemplateInUse,
  );

  const fromBusinessLabel: string = isManual ? "Billing From" : "Paid To";
  const toBusinessLabel: string = isManual ? "Billing To" : "Paid From";

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business filter configuration
  const defaultToBusinessFilter: Array<any> = getBusinessFilters(
    activeSundryTemplate?.paidFromSearchFilter,
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Business filter configuration
  const defaultFromBusinessFilter: Array<any> = getBusinessFilters(
    activeSundryTemplate?.paidToSearchFilter,
  );

  const viewableAttachments = isEdit ? attachments : parkedAttachmentFiles;

  const onChangeExtra = sTId => {
    const context = {
      sundryTemplateId: sTId,
      createAnother,
    };

    dispatch(setModalContext(ModalTypes.ManualAdjustmentForm, context));
  };

  const handleUpload = useCallback(
    file => {
      if (isEdit) {
        const formData = {
          manual_adjustment: id,
          type: DocumentTypes.SUNDRY,
        };

        dispatch(uploadRequest(file, formData));
      } else {
        setParkedAttachmentFiles(uniq([...parkedAttachmentFiles, file]));
      }
    },
    [dispatch, id, isEdit, parkedAttachmentFiles, setParkedAttachmentFiles],
  );

  return (
    <Form>
      <DialogTitle onClose={onClose}>
        {id && (
          <AuditLogLink
            auditLogType={AuditLogTypes.MANUAL_ADJUSTMENT}
            dataId={id}
            returnTo={window.location.hash}
          />
        )}
        &nbsp;{isEdit ? "Edit Sundry" : "Create Sundry"}
        <AdminLink model="manualadjustment" cbid={id} />
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SelectField
              autoFocus
              required
              name="sundryTemplateId"
              options={sundryTemplateOptions}
              label="Type"
              onChangeExtra={onChangeExtra}
              disabled={isEdit}
            />
          </Grid>

          <Grid container item xs={12}>
            <Grid item xs={9}>
              <BusinessFieldWithFilter
                label={fromBusinessLabel}
                name="fromBusinessId"
                disabled={readOnlyFields.includes("fromBusinessId")}
                defaultBusinessFilter={defaultFromBusinessFilter}
              />
              <SmallText>GST Status - {fromBusinessGSTStatus}</SmallText>
            </Grid>
            <Grid item xs={3} container alignItems="center">
              <SecondaryButton
                type="button"
                onClick={openEditFromBusiness}
                disabled={!fromBusinessId}
              >
                Edit Business
              </SecondaryButton>
            </Grid>
          </Grid>

          <Grid container item xs={12}>
            <Grid item xs={9}>
              <BusinessFieldWithFilter
                label={toBusinessLabel}
                name="toBusinessId"
                disabled={readOnlyFields.includes("toBusinessId")}
                defaultBusinessFilter={defaultToBusinessFilter}
              />

              <SmallText>&nbsp;</SmallText>
            </Grid>
            <Grid item xs={3} container alignItems="center">
              <SecondaryButton
                type="button"
                onClick={openEditToBusiness}
                disabled={!toBusinessId}
              >
                Edit Business
              </SecondaryButton>
            </Grid>
          </Grid>

          <SundryPricingForm readOnlyFields={readOnlyFields} />
          {renderHelpText && (
            <Grid item xs={12}>
              <SundryHelpText
                template={activeSundryTemplate}
                amount={totalInc}
                paidFromBusinessName={toBusiness?.name}
                paidToBusinessName={fromBusiness?.name}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextArea
              label="Description"
              name="note"
              disabled={readOnlyFields.includes("note")}
              tooltip="This will appear on RCTIs and/or Invoices"
              required={isTemplateInUse}
            />
          </Grid>
          {!isManual && (
            <>
              <Grid item xs={6}>
                <SelectField
                  label="GL Code"
                  name="glCode"
                  disabled={readOnlyFields.includes("glCode")}
                  options={ledgerAccountOptions}
                  menuPortalTarget={document.body}
                  required={isTemplateInUse}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectField
                  label="Tax Type"
                  name="taxType"
                  disabled={readOnlyFields.includes("taxType")}
                  options={BaseTaxTypeOptions}
                  menuPortalTarget={document.body}
                  required={isTemplateInUse}
                />
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <Input
              label="External Ref/s"
              name="billingReference"
              maxLength={255}
            />
          </Grid>

          <Grid item xs={6}>
            <ManualChargeLabelSelectorField
              label="Labels"
              name="labels"
              disabled={readOnlyFields.includes("labels")}
            />
          </Grid>
          <Grid item xs={12}>
            <Divider />
            <FileInput
              upload={handleUpload}
              closeSelf={EMPTY_OBJECT_FUNCTION}
              dropZoneBackgroundColor={theme.colors.gray95}
            />
          </Grid>

          {viewableAttachments.length > 0 && (
            <>
              <Grid item xs={12}>
                <Label>Send uploaded Sundry files to</Label>
              </Grid>

              <Grid item xs={6}>
                <CheckBox
                  name="emailAttachmentsToFromBusiness"
                  label={fromBusiness?.name || `${fromBusinessLabel} Business`}
                  dataTour="emailAttachmentsToFromBusiness"
                />
              </Grid>
              <Grid item xs={6}>
                <CheckBox
                  name="emailAttachmentsToToBusiness"
                  label={toBusiness?.name || `'${toBusinessLabel}' Business`}
                  dataTour="emailAttachmentsToToBusiness"
                />
              </Grid>
            </>
          )}

          <Row>
            {viewableAttachments.map(attachment => (
              <Cell key={attachment.id}>
                <DeletableThumbnail
                  handleDelete={() => {
                    setDeleteFileDialog(attachment);
                  }}
                  file={attachment}
                  setPreview={setPreview}
                  readOnly={false}
                />
              </Cell>
            ))}
          </Row>
        </Grid>
        {!isEdit && (
          <>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Row justifyStart alignEnd>
              <CheckBox name="createAnother" label="Add Another On Submit" />
            </Row>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {id && (
          <DeleteButton type="button" onClick={showConfirmDelete}>
            Delete
          </DeleteButton>
        )}

        <SecondaryButton type="button" onClick={onClose}>
          Cancel
        </SecondaryButton>
        <Button data-tour="adjust" type="submit">
          Submit
        </Button>
      </DialogActions>
      <ConfirmDialog
        title={createModalTitle("this Sundry")}
        isOpen={isShowingConfirmDelete}
        onCancel={hideConfirmDelete}
        onDelete={onConfirmDelete}
      />
    </Form>
  );
};

export default SundryForm;
